/**
 * <LiveViewSwitch> — au téléphone, l'activité ou la visio : la bascule de l'en-tête du live (molecule).
 *
 * Un téléphone ne tient qu'une vue à la fois (Wajih, 17/10) : deux segments, « Activité » et
 * « Visio ». La mini-visio du masteur et son prénom, qui tenaient le second segment, sont partis
 * dans la bulle tandem (`<LiveTandemBubble>`, Wajih 19/10) : c'est elle, en bas à droite, qui dit le
 * mode (qui est devant, et le liseré du visage du masteur) et mène aussi à la visio.
 *
 * Le segment choisi prend l'aplat d'une option choisie (`role-selected`), libellé blanc ; l'autre reste à
 * même la piste, à l'encre secondaire (`liveViewSwitchFace`, Wajih, daily du 23/10 : écrit en orange lui
 * aussi, il se confondait avec le choisi en clair). **L'aplat glisse** d'un segment à l'autre, à la durée
 * `standard` et à la courbe de la marque ; le libellé blanc ne se voit que dans l'aplat (une copie des
 * libellés, découpée par lui), si bien qu'il change avec lui, sans saut. En mouvement réduit, il est
 * aussitôt à sa place.
 *
 * Au clavier (le web), le segment qui a le focus porte l'anneau jaune du livre (`<FocusRing>`).
 *
 * Contrôlée : la vue ouverte arrive en props, le toucher remonte.
 */
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Animated, Easing, Platform, Pressable, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { type UILiveView } from "../../../ports/primary/ui/types/studentLive"
import { LIVE_VIEW_SWITCH_FACE as FACE } from "./liveViewSwitchFace"

// Ce que montre un téléphone : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UILiveView }

export type LiveViewSwitchProps = {
    view: UILiveView
    onChange: (view: UILiveView) => void
    testID?: string
}

/** La cible d'un doigt. */
const TOUCH_TARGET = 44

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean }

const SEGMENTS: { view: UILiveView; label: string; icon: "tasks" | "video" }[] = [
    { view: "activity", label: "Activité", icon: "tasks" },
    { view: "stage", label: "Visio", icon: "video" },
]

/** L'icône et le libellé d'un segment, dans une encre. */
function SegmentFace(props: { segment: (typeof SEGMENTS)[number]; ink: SemanticColorKey }) {
    const { segment, ink } = props
    return (
        <>
            <Icon name={segment.icon} size={16} color={ink} />
            <Text size="text-md" weight="bold" color={ink} numberOfLines={1} style={styles.label}>
                {segment.label}
            </Text>
        </>
    )
}

export function LiveViewSwitch(props: LiveViewSwitchProps) {
    const { view, onChange, testID } = props
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const chosen = Math.max(
        0,
        SEGMENTS.findIndex((segment) => segment.view === view)
    )

    // État d'interface : la largeur de la piste, où l'aplat se mesure et glisse.
    const [trackWidth, setTrackWidth] = useState<number | null>(null)
    // 0 sur « Activité », 1 sur « Visio ».
    const slide = useRef(new Animated.Value(chosen)).current
    useEffect(() => {
        if (reduceMotion) {
            slide.setValue(chosen)
            return undefined
        }
        const move = Animated.timing(slide, {
            toValue: chosen,
            duration: theme.motion.standard,
            easing: Easing.bezier(...theme.motion.easing),
            // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
            useNativeDriver: Platform.OS !== "web",
        })
        move.start()
        return () => move.stop()
    }, [chosen, reduceMotion, slide, theme.motion])

    const inset = theme.spacing["1"]
    const inner = trackWidth != null ? trackWidth - 2 * inset : null
    const segmentWidth = inner != null ? (inner - inset) / 2 : null
    const step = segmentWidth != null ? segmentWidth + inset : 0
    // Gardées d'un rendu à l'autre : une interpolation neuve se rattacherait au pilote natif à chaque rendu.
    const glide = useMemo(
        () => ({
            chosen: { transform: [{ translateX: slide.interpolate({ inputRange: [0, 1], outputRange: [0, step] }) }] },
            faces: { transform: [{ translateX: slide.interpolate({ inputRange: [0, 1], outputRange: [0, -step] }) }] },
        }),
        [slide, step]
    )

    return (
        <View
            style={styles.track}
            testID={testID}
            accessibilityRole="tablist"
            onLayout={(event) => setTrackWidth(event.nativeEvent.layout.width)}
        >
            {SEGMENTS.map((segment, index) => {
                const selected = index === chosen
                // Avant la mesure, le segment choisi porte l'aplat lui-même, sans glisser.
                const still = selected && segmentWidth == null
                return (
                    <Pressable
                        key={segment.view}
                        onPress={() => onChange(segment.view)}
                        accessibilityRole="tab"
                        accessibilityState={{ selected }}
                        // Le web ne lit pas `accessibilityState` : l'onglet choisi s'y dit par l'attribut ARIA.
                        aria-selected={selected}
                        accessibilityLabel={segment.label}
                        style={({ pressed }: PressableVisualState) => [
                            styles.face,
                            styles.segment(still),
                            focusRingHost,
                            pressed && styles.pressed,
                        ]}
                    >
                        {({ focused }: PressableVisualState) => (
                            <>
                                <FocusRing visible={focused === true} radius={theme.radius.full} />
                                <SegmentFace segment={segment} ink={still ? FACE.chosenInk : FACE.otherInk} />
                            </>
                        )}
                    </Pressable>
                )
            })}
            {segmentWidth != null && inner != null && (
                // L'aplat, par-dessus les segments sans prendre leur toucher : il porte les libellés en blanc,
                // posés exactement sur ceux de la piste et découpés par lui.
                <Animated.View
                    pointerEvents="none"
                    aria-hidden
                    importantForAccessibility="no-hide-descendants"
                    style={[styles.chosen(segmentWidth), glide.chosen]}
                >
                    <Animated.View style={[styles.chosenFaces(inner), glide.faces]}>
                        {SEGMENTS.map((segment) => (
                            <View key={segment.view} style={styles.face}>
                                <SegmentFace segment={segment} ink={FACE.chosenInk} />
                            </View>
                        ))}
                    </Animated.View>
                </Animated.View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    track: {
        flexDirection: "row",
        gap: theme.spacing["1"],
        padding: theme.spacing["1"],
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors[FACE.track],
    },
    /** Un segment : la place de son icône et de son libellé (`face`), à la cible d'un doigt. */
    segment: (still: boolean) => ({
        minHeight: TOUCH_TARGET,
        borderRadius: theme.radius.full,
        backgroundColor: still ? theme.colors[FACE.chosen] : "transparent",
    }),
    /** L'aplat du segment choisi, à la place du premier segment ; il glisse vers le second. */
    chosen: (width: number) => ({
        position: "absolute" as const,
        top: theme.spacing["1"],
        bottom: theme.spacing["1"],
        left: theme.spacing["1"],
        width,
        borderRadius: theme.radius.full,
        overflow: "hidden" as const,
        backgroundColor: theme.colors[FACE.chosen],
    }),
    /** Les libellés en blanc, à la largeur de la piste : ils restent sur ceux de dessous. */
    chosenFaces: (width: number) => ({
        position: "absolute" as const,
        top: 0,
        bottom: 0,
        left: 0,
        width,
        flexDirection: "row" as const,
        gap: theme.spacing["1"],
    }),
    /** L'icône et le libellé d'un segment, centrés : sur la piste comme dans l'aplat. */
    face: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing["2"],
        paddingHorizontal: theme.spacing["2"],
    },
    label: { flexShrink: 1 },
    /** Le retour de pression d'un segment : celui d'une ligne qui presse (`<SettingRow>`). */
    pressed: { opacity: 0.6 },
}))
